import { VossVersionStatus } from '../../../Constants/VOSS/vossVersionStatus';
import { VossPackageTypeText, type VossPackageType } from '../../../Constants/VOSS/vossPackageType';
import type { StatusLabelVariant } from '../../Fundamentals/Label/StatusLabel';
import dateUtils from '../../../Utils/dateUtils';
import type { PackageFlagField } from './Packages.types';

export const packageStatusConfig: Record<VossVersionStatus, { labelKey: string; variant: StatusLabelVariant; disabled: boolean }> = {
    [VossVersionStatus.Draft]: { labelKey: 'packages.statusDraft', variant: 'default', disabled: false },
    [VossVersionStatus.Staged]: { labelKey: 'packages.statusStaged', variant: 'info', disabled: false },
    [VossVersionStatus.Published]: { labelKey: 'packages.statusPublished', variant: 'success', disabled: false },
    [VossVersionStatus.Archived]: { labelKey: 'packages.statusArchived', variant: 'default', disabled: true },
    [VossVersionStatus.Discarded]: { labelKey: 'packages.statusDiscarded', variant: 'danger', disabled: false },
};

export const packageStatuses = Object.keys(packageStatusConfig).map(Number) as VossVersionStatus[];

export const packageTypes = Object.keys(VossPackageTypeText).map(Number) as VossPackageType[];

// Route segments stay English to match the rest of /settings; labels come from i18n.
// `countOn` names the PackageDetail array whose length shows as the tab's badge.
export const PACKAGE_DETAIL_TABS: { path: string; labelKey: string; icon: string; countOn?: 'plans' | 'tiers' | 'salesChannels' | 'chargePlans' | 'scheduledChanges' }[] = [
    { path: '', labelKey: 'packages.tabOverview', icon: 'overview' },
    { path: 'plans', labelKey: 'packages.tabPlans', icon: 'credit-card', countOn: 'plans' },
    { path: 'tiers', labelKey: 'packages.tabTiers', icon: 'list-ordered', countOn: 'tiers' },
    { path: 'sales-channels', labelKey: 'packages.tabSalesChannels', icon: 'users', countOn: 'salesChannels' },
    { path: 'charge-plans', labelKey: 'packages.tabChargePlans', icon: 'spreadsheet', countOn: 'chargePlans' },
    { path: 'scheduled-changes', labelKey: 'packages.tabScheduledChanges', icon: 'calendar-time', countOn: 'scheduledChanges' },
];

// the list endpoint sends `type` as a string, the detail endpoint as the enum's number — handle both
export const packageTypeLabel = (type: string | number | null | undefined): string => {
    if (typeof type === 'number') return VossPackageTypeText[type as VossPackageType] ?? '';

    return type ?? '';
};

export const PACKAGE_FLAGS: {
    field: PackageFlagField;
    labelKey: string;
    descriptionKey: string;
}[] = [
    { field: 'excludeNameLineFromInvoice', labelKey: 'packages.flagExcludeNameLine', descriptionKey: 'packages.flagExcludeNameLineDescription' },
    { field: 'excludeFromUnpaidSubscriptionTermination', labelKey: 'packages.flagExcludeUnpaidTermination', descriptionKey: 'packages.flagExcludeUnpaidTerminationDescription' },
    { field: 'autoVerifyCustomer', labelKey: 'packages.flagAutoVerifyCustomer', descriptionKey: 'packages.flagAutoVerifyCustomerDescription' },
];

// VOSS only accepts Name, ExternalId and TargetGroups once a version leaves Draft
export const isFullyEditable = (status: VossVersionStatus | undefined): boolean => status === VossVersionStatus.Draft;

// VOSS rejects anything else; see the status description on UpdatePackageRequest
const ALLOWED_TRANSITIONS: Record<VossVersionStatus, VossVersionStatus[]> = {
    [VossVersionStatus.Draft]: [VossVersionStatus.Staged, VossVersionStatus.Discarded],
    [VossVersionStatus.Staged]: [VossVersionStatus.Published, VossVersionStatus.Discarded],
    [VossVersionStatus.Published]: [VossVersionStatus.Archived],
    [VossVersionStatus.Archived]: [],
    [VossVersionStatus.Discarded]: [VossVersionStatus.Draft],
};

export const allowedTransitions = (current: VossVersionStatus | undefined): VossVersionStatus[] => (current === undefined ? [] : (ALLOWED_TRANSITIONS[current] ?? []));

export const SalesChannelStatus = { Active: 'active', Planned: 'planned', Expired: 'expired' } as const;
export type SalesChannelStatus = (typeof SalesChannelStatus)[keyof typeof SalesChannelStatus];

// borrows the version badge looks so every status on the package page renders the same way
export const salesChannelStatusConfig: Record<SalesChannelStatus, { labelKey: string; badgeStatus: VossVersionStatus }> = {
    [SalesChannelStatus.Active]: { labelKey: 'packages.salesChannelStatusActive', badgeStatus: VossVersionStatus.Published },
    [SalesChannelStatus.Planned]: { labelKey: 'packages.salesChannelStatusPlanned', badgeStatus: VossVersionStatus.Staged },
    [SalesChannelStatus.Expired]: { labelKey: 'packages.salesChannelStatusExpired', badgeStatus: VossVersionStatus.Archived },
};

export const salesChannelStatus = (validFrom: string | null | undefined, validTo: string | null | undefined): SalesChannelStatus => {
    const today = dateUtils.getCurrentDate();

    if (validFrom !== null && validFrom !== undefined && dateUtils.formatDate(validFrom) > today) return SalesChannelStatus.Planned;
    if (validTo !== null && validTo !== undefined && dateUtils.formatDate(validTo) < today) return SalesChannelStatus.Expired;

    return SalesChannelStatus.Active;
};
